<template>
  <transition name="dialog-fade" @after-leave="afterLeave" @after-enter="afterEnter">
    <div
      v-show="visible"
      class="pdl-dialog__wrapper"
      @click.self="handleWrapperClick"
      :aria-labelledby="labelledbyID"
      role="dialog"
    >
      <div
        ref="dialog"
        class="pdl-dialog shadow-lg"
        qaid="pdl-dialog"
        :class="[
          {
            'is-fullscreen': fullscreen,
            'text-center': center,
          },
          customClass,
        ]"
        :style="style"
      >
        <div class="pdl-dialog__header" :class="[headerClass, {'pdl-dialog__header--padding-right': showClose}]">
          <!-- @slot Content of the dialog title. -->
          <slot name="title">
            <span class="pdl-dialog__title pdl-heading pdl-heading--md" qaid="dialog-title" :id="labelledbyID">
              {{ title }}
            </span>
          </slot>
          <pdl-button
            v-if="showClose"
            text
            icon-only
            icon="close"
            class="pdl-dialog__close"
            :label="closeDialogToolTip"
            qaid="dialog-close"
            @click="handleClose"
          />
        </div>

        <div v-if="rendered" class="pdl-dialog__body" :class="[bodyClass]">
          <!-- @slot Content of the dialog body. -->
          <slot></slot>
        </div>

        <div v-if="$slots.footer" class="pdl-dialog__footer" :class="[footerClass]">
          <!-- @slot Content of the dialog footer. -->
          <slot name="footer"></slot>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import {PdlPopupUtil} from '@pedal/pdl-shared-libraries';
import {PdlButton} from '@pedal/pdl-button';

export default {
  name: 'PdlDialog',
  components: {PdlButton},
  mixins: [PdlPopupUtil],
  props: {
    /**
     * Title of dialog.
     */
    title: {
      type: String,
      default: '',
    },
    /**
     * Display of semi-transparent overlay over page.
     */
    modal: {
      type: Boolean,
      default: true,
    },
    /**
     * Whether to append modal to body element. If `false`, the modal will be appended to dialog's parent element
     */
    modalAppendToBody: {
      type: Boolean,
      default: true,
    },
    /**
     * Whether to append dialog itself to body. A nested dialog should have this attribute set to `true`.
     */
    appendToBody: {
      type: Boolean,
      default: false,
    },
    /**
     * Whether scroll of body is disabled while dialog is displayed.
     */
    lockScroll: {
      type: Boolean,
      default: true,
    },
    /**
     * Whether the dialog can be closed by clicking the overlay.
     */
    closeOnClickModal: {
      type: Boolean,
      default: true,
    },
    /**
     * Whether the dialog can be closed by pressing ESC key.
     */
    closeOnPressEscape: {
      type: Boolean,
      default: true,
    },
    /**
     * Display a close button.
     */
    showClose: {
      type: Boolean,
      default: true,
    },
    /**
     * Width of dialog.
     */
    width: {
      type: String,
      default: undefined,
    },
    /**
     * Whether the dialog takes up full screen.
     */
    fullscreen: Boolean,
    /**
     * Custom class for dialog.
     */
    customClass: {
      type: String,
      default: '',
    },
    /**
     * Custom class for heading container.
     */
    headerClass: {
      type: String,
      default: '',
    },
    /**
     * Custom class for body container.
     */
    bodyClass: {
      type: String,
      default: '',
    },
    /**
     * Custom class for footer container.
     */
    footerClass: {
      type: String,
      default: '',
    },
    /**
     * Value for margin-top of dialog.
     */
    top: {
      type: String,
      default: '15vh',
    },
    /**
     * Horizontally center align the header, body and footer content.
     */
    center: {
      type: Boolean,
      default: false,
    },
    /**
     * Accessible aria-label text for close button.
     */
    closeDialogToolTip: {
      type: String,
      default: 'Close',
    },
  },
  data() {
    return {
      closed: false,
      lastFocusedElement: null,
    };
  },
  computed: {
    style() {
      let style = {};
      if (!this.fullscreen) {
        style.marginTop = this.top;
        if (this.width) {
          style.width = this.width;
        }
      }
      return style;
    },
    labelledbyID() {
      return this.$attrs.id ? `${this.$attrs.id}-title` : `${crypto.randomUUID()}-title`;
    },
  },
  watch: {
    visible(val) {
      if (val) {
        this.closed = false;
        this.lastFocusedElement = document.activeElement;
        /**
         * Emitted when dialog opens.
         */
        this.$emit('open');
        this.$nextTick(() => {
          this.$refs.dialog.scrollTop = 0;
        });
        if (this.appendToBody) {
          document.body.appendChild(this.$el);
        }
      } else {
        /**
         * Emitted when dialog closes.
         */
        if (!this.closed) this.$emit('close');
      }
    },
  },
  mounted() {
    if (this.visible) {
      this.rendered = true;
      this.open();
      if (this.appendToBody) {
        document.body.appendChild(this.$el);
      }
    }
  },
  destroyed() {
    // if appendToBody is true, remove DOM node after destroy
    if (this.appendToBody && this.$el && this.$el.parentNode) {
      this.$el.parentNode.removeChild(this.$el);
    }
  },
  methods: {
    handleWrapperClick() {
      if (!this.closeOnClickModal) return;
      this.handleClose();
    },
    handleClose() {
      this.hide();
    },
    hide(cancel) {
      if (cancel !== false) {
        this.$emit('update:visible', false);
        /**
         * Emitted when dialog closes.
         */
        this.$emit('close');
        this.closed = true;
      }
    },
    afterLeave() {
      /**
       * Emitted when dialog closing animation ends.
       */
      this.$emit('closed');
      this.revertFocusBacktoLastFocusedElement();
    },
    revertFocusBacktoLastFocusedElement() {
      if (document.contains(this.lastFocusedElement)) {
        this.lastFocusedElement.focus();
      } else {
        document.body.focus();
      }
      this.lastFocusedElement = null;
    },
    afterEnter() {
      this.focusFirstFocusableElement();
      this.$emit('opened');
    },
  },
};
</script>

<style lang="scss">
@import 'Dialog.scss';
</style>
